import { IonBackButton, IonButtons, IonCard, IonCol, IonContent, IonGrid, IonHeader, IonPage, IonRow, IonTitle, IonToolbar } from "@ionic/react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@apollo/client";
import { userQuery } from "../../apollo-client/queries";
import { FidelityCustomer, FidelityPrepaidAccount } from "../../apollo-client/types";
import { useTenant } from "../../hooks/multiTenancy";
import { getPrepaidAccount, getPrice } from "../../utils";
import CreditTransactions from "../../components/creditTransactions";
import "./myCredit.css";
import { useSelectedSalesPoint } from "../../hooks/selectedSalesPoint";

type SalesPointBalance = {
  id: number;
  salesPointId: number;
  salesPointName: string;
  salesPointStreet: string | undefined;
  salesPointCity: string | undefined;
  salesPointDistrict: string | undefined;
  salesPointPrepaidAccount: FidelityPrepaidAccount;
}

const MyCredit: React.FC = () => {
  const {t} = useTranslation();
  const tenant = useTenant();
  const userResponse = useQuery(userQuery);
  const user: FidelityCustomer = userResponse.data?.user;
  const [selectedSalesPointId] = useSelectedSalesPoint();
  const fidelitySalesPoints = tenant.settings?.mobileCommerceSalesPoints ?? [];
  const balances = 
    user
    ? fidelitySalesPoints
        .map((p) => ({
          id: p.id,
          salesPointId: p.salesPoint.id,
          salesPointName: p.name,
          salesPointStreet: p.street,
          salesPointCity: p.city,
          salesPointDistrict: p.district,
          salesPointPrepaidAccount: getPrepaidAccount(user, p.id)
        }))
        .filter((b): b is SalesPointBalance => !!b.salesPointName && !!b.salesPointPrepaidAccount)
    : [];
  const selectedSalesPointBalance = balances.find((b) => b.salesPointId === selectedSalesPointId);
  const otherSalesPointBalances = balances.filter((b) => b.salesPointId !== selectedSalesPointId);
  const reorderedBalances = selectedSalesPointBalance ? [selectedSalesPointBalance, ...otherSalesPointBalances] : otherSalesPointBalances;
  return (
    <IonPage className="mainSelection">
      <IonHeader>
        <IonToolbar color="secondary">
          <IonButtons slot="start">
            <IonBackButton />
          </IonButtons>
          <IonTitle>{t("myCredit.title")}</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent fullscreen className="pageContainer">
        {reorderedBalances.length === 0 && <div className="no-results-placeholder">{t("myCredit.no_credit")}</div>}
        {reorderedBalances.length > 0 && (
            <IonGrid>
              <IonRow>
                {reorderedBalances.map((b) => 
                    <IonCol key={b.id} size="12">
                      <IonCard className="header">
                        <div className="section sales-point">
                          <div className="sales-point-name">{b.salesPointName}</div>
                          {
                            b.salesPointStreet 
                            && b.salesPointCity 
                            && b.salesPointDistrict
                            && <div className="sales-point-address">{b.salesPointStreet} {b.salesPointCity} {b.salesPointDistrict}</div>
                          }
                        </div>
                        <div className="section section-horizontal">
                          <div className="title">{t("myCredit.credit")}</div>
                          <div className="value credit">{getPrice(b.salesPointPrepaidAccount.amount, tenant.settings?.fidelityAccount?.account.currency)}</div>
                        </div>
                        <CreditTransactions idFidelityPrepaidAccount={b.salesPointPrepaidAccount.id} fidelitySalesPointId={b.id} />
                      </IonCard>
                    </IonCol>
                )}
              </IonRow>
            </IonGrid>
        )}
      </IonContent>
    </IonPage>
  );
};

export default MyCredit;
